import httpClient from 'utility/httpClient.util'
import {
  getDesktopDevice,
  printReportOnDesktop,
} from 'utility/desktopPrint.util'
import { pingDesktopService } from 'utility/desktopService.util'

export const desktopIdentity = (device) => ({
  machineName: String(device?.machineName || '').trim(),
  deviceId:
    device?.desktopDeviceId == null
      ? null
      : String(device.desktopDeviceId).trim() || null,
})

export const sameDesktop = (left, right) => {
  const a = desktopIdentity(left)
  const b = desktopIdentity(right)
  return a.deviceId || b.deviceId
    ? Boolean(a.deviceId && b.deviceId && a.deviceId === b.deviceId)
    : Boolean(a.machineName && a.machineName === b.machineName)
}

// This is a local opt-in hint only. BE remains the source of printer settings,
// and the Desktop identity must be checked again before using the preference.
const reportPrintHintKey = (reportCode) => {
  const backend = new URL(
    httpClient.defaults?.baseURL || window.location.origin,
    window.location.origin,
  ).href.replace(/\/+$/, '')
  return `report-desktop-auto-print:v1:${backend}:${encodeURIComponent(reportCode)}`
}

const readReportPrintHint = (reportCode) => {
  if (!reportCode) return null
  try {
    const hint = JSON.parse(localStorage.getItem(reportPrintHintKey(reportCode)))
    return desktopIdentity(hint).machineName ? hint : null
  } catch {
    // A missing/unavailable browser preference means preview without Desktop.
    return null
  }
}

const rememberReportPrintHint = (device, reportCode, config) => {
  if (!reportCode) return
  try {
    const key = reportPrintHintKey(reportCode)
    const identity = desktopIdentity(device)
    if (config?.autoPrint === true && identity.machineName) {
      localStorage.setItem(key, JSON.stringify({
        machineName: identity.machineName,
        desktopDeviceId: identity.deviceId,
      }))
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    // Printer setup and preview still work when browser storage is disabled.
  }
}

export const isPrintIntent = (query = {}) =>
  query.printIntent !== 'preview' &&
  (query.printIntent === 'print' || query.autoPrint === 'true')

// Rendering on the Report server must never connect to its local Desktop or
// repeat the caller's print command.
export const reportPdfRenderQuery = (input) => {
  const query = new URLSearchParams(String(input || '').replace(/^[?&]+/, ''))
  ;[
    'deviceId',
    'machineName',
    'printIntent',
    'autoPrint',
    'browserPrint',
  ].forEach((key) => query.delete(key))
  query.set('showOnlyPrintArea', 'true')
  query.set('printIntent', 'preview')
  query.set('autoPrint', 'false')
  return query.toString()
}

export const prepareReportWithPrintConfig = async (
  url,
  { rendering = false } = {},
) => {
  const params = Object.fromEntries(
    new URL(url, window.location.origin).searchParams,
  )
  const reportCode = params.reportCode
  const reportTemplateId = Number(params.reportTemplateId) || undefined
  const revisionId = Number(params.revisionId) || undefined
  ;[
    'reportCode',
    'reportTemplateId',
    'revisionId',
    'deviceId',
    'machineName',
    'printConfig',
  ].forEach((key) => delete params[key])
  let report
  try {
    const { data } = await httpClient.post('/report/report-webview/prepare', {
      ...(reportCode ? { reportCode } : {}),
      ...(reportTemplateId ? { reportTemplateId } : {}),
      ...(revisionId ? { revisionId } : {}),
      params,
    })
    report = data.data.report
  } catch (error) {
    // Permit preview while the backend is being rolled out. Do not hide real
    // validation/database/permission errors as a missing printer preference.
    const message = error?.response?.data?.message
    const missingRoute =
      error?.response?.status === 404 &&
      (!message || /^Cannot POST\b/.test(String(message)))
    if (!missingRoute) throw error
    const { data } = await httpClient.get(url)
    return {
      ...data.data,
      desktopPrintConfig: null,
      desktopPrintDevice: null,
    }
  }

  const code = report?.reportCode || report?.reportTemplate?.code || reportCode
  const hint = !rendering && readReportPrintHint(code)
  let device = null
  let printConfig = null
  if (hint) {
    try {
      if (await pingDesktopService()) {
        const current = await getDesktopDevice()
        if (sameDesktop(hint, current)) {
          printConfig = await resolveReportPrintConfig(current, code)
          device = current
        } else {
          rememberReportPrintHint(null, code, null)
        }
      }
    } catch (error) {
      // Optional printing must not prevent the report preview from opening.
      console.warn('Report Desktop settings unavailable', error)
    }
  }
  return {
    ...report,
    desktopPrintConfig: printConfig,
    desktopPrintDevice: device,
  }
}

export const resolveReportPrintConfig = async (device, reportCode) => {
  const identity = desktopIdentity(device)
  if (!identity.machineName || !reportCode)
    throw new Error('ไม่พบชื่อเครื่องหรือรหัสรายงาน')
  const { data } = await httpClient.post(
    '/report/report-print-config/resolve',
    { ...identity, reportCode },
  )
  rememberReportPrintHint(device, reportCode, data.data.printConfig)
  return data.data.printConfig
}

export const saveReportPrintConfig = async (device, reportCode, settings) => {
  const { data } = await httpClient.post('/report/report-print-config', {
    ...desktopIdentity(device),
    reportCode,
    ...settings,
  })
  rememberReportPrintHint(device, reportCode, data.data.printConfig)
  return data.data.printConfig
}

export const deleteReportPrintConfig = async (device, reportCode) => {
  const identity = desktopIdentity(device)
  if (!identity.machineName || !reportCode)
    throw new Error('ไม่พบชื่อเครื่องหรือรหัสรายงาน')
  const { data } = await httpClient.delete('/report/report-print-config', {
    data: { ...identity, reportCode },
  })
  rememberReportPrintHint(device, reportCode, null)
  return data.data
}

export const printConfiguredReport = async ({
  device,
  reportCode,
  getPdfBlob,
  paperSize,
  force = false,
  settings = null,
}) => {
  const current = await getDesktopDevice()
  if (!sameDesktop(device, current))
    throw new Error('เครื่อง Desktop เปลี่ยน กรุณาเปิดตั้งค่าการพิมพ์อีกครั้ง')
  // A diagnostic print uses the current modal selection without saving it.
  // Automatic printing still resolves the persisted preference every time.
  const config = force && settings
    ? settings
    : await resolveReportPrintConfig(current, reportCode)
  if (!config || (!force && !config.autoPrint)) return false
  const printer = current.printers.find(
    (item) => (item.refId || item.name) === config.printerRefId,
  )
  if (!printer)
    throw new Error('ไม่พบเครื่องพิมพ์ที่ตั้งค่าไว้ กรุณาตั้งค่าการพิมพ์ใหม่')
  if (
    ['offline', 'error', 'unavailable'].includes(
      String(printer.status).toLowerCase(),
    )
  ) {
    throw new Error('เครื่องพิมพ์ที่ตั้งค่าไว้ไม่พร้อมใช้งาน')
  }
  const blob = await getPdfBlob()
  // PDF generation can take time. Check the current device and selected queue
  // again before handing any bytes to the local service.
  const ready = await getDesktopDevice()
  if (!sameDesktop(current, ready))
    throw new Error('เครื่อง Desktop เปลี่ยนระหว่างเตรียมเอกสาร กรุณาลองใหม่')
  const target = ready.printers.find(
    (item) => (item.refId || item.name) === config.printerRefId,
  )
  if (
    !target ||
    ['offline', 'error', 'unavailable'].includes(
      String(target.status).toLowerCase(),
    )
  ) {
    throw new Error('เครื่องพิมพ์ที่ตั้งค่าไว้ไม่พร้อมใช้งาน')
  }
  await printReportOnDesktop({
    ...(target.refId ? { refId: target.refId } : { printerName: target.name }),
    copies: config.copies,
    ...(paperSize ? { paperSize } : {}),
    blob,
  })
  return true
}
